/**
 * Which physical hand does what.
 *
 * The scheme gives the two hands different jobs — the off hand says where you are and what
 * you want, the dominant hand does the work — so every gesture needs to know which is which.
 * Keeping that in one place is what stops it leaking: gestures ask for `dominant` or `off`
 * and get whichever controller currently means that, so switching handedness is a single
 * lookup rather than a special case repeated in each of them.
 *
 * It is switchable because it is an accessibility problem rather than a preference. A
 * left-handed user with fine manipulation bound to their off hand finds the whole thing
 * clumsy, and there is no way to guess which hand they favour.
 *
 * A module-level setting rather than a constructor argument, deliberately: it is a property
 * of the person using the browser, not of any one scene, and threading it through eight
 * constructors would only obscure that.
 */

export type DominantHand = 'left' | 'right';

export type HandRole = 'dominant' | 'off';

const STORAGE_KEY = 'tunet:dominant-hand';

// spec: SPEC-080
class HandednessSetting {
  private value: DominantHand = loadStored();
  private readonly listeners = new Set<(dominant: DominantHand) => void>();

  get dominant(): DominantHand {
    return this.value;
  }

  get off(): DominantHand {
    return this.value === 'right' ? 'left' : 'right';
  }

  set(dominant: DominantHand): void {
    if (dominant === this.value) {
      return;
    }

    this.value = dominant;

    try {
      localStorage.setItem(STORAGE_KEY, dominant);
    } catch {
      // Private browsing refuses storage. The setting still applies for this session.
    }

    for (const listener of this.listeners) {
      listener(dominant);
    }
  }

  /** Notified when the setting changes, so live bindings can follow it. Returns a disposer. */
  onChanged(listener: (dominant: DominantHand) => void): () => void {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  }

  /**
   * What role a tracked input plays.
   *
   * The desktop pointer reports `none` and counts as the dominant hand: there is only one of
   * it, and manipulation is what it does. The off-hand bindings reach a desktop another way —
   * the tray opens with Tab, travel is WASD.
   */
  roleOf(handedness: XRHandedness): HandRole {
    return handedness === this.off ? 'off' : 'dominant';
  }
}

function loadStored(): DominantHand {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === 'left' || stored === 'right') {
      return stored;
    }
  } catch {
    // Storage unavailable; fall through to the default.
  }

  return 'right';
}

export const handedness = new HandednessSetting();
